/**
 * Handoff of the signed-in user to AppLauncher.
 *
 * AppLauncher is a separate origin, so nothing about this session reaches it on its own — a browser's
 * token cache never crosses an origin boundary. What it can use is the Entra session the browser already
 * holds: given the username, it attempts a non-interactive sign-in and the user lands on the page already
 * authenticated instead of looking at a sign-in button.
 *
 * The hint is an optimisation on both ends. AppLauncher falls back to its ordinary sign-in when the hint
 * is absent, stale, or when the browser blocks the third-party cookie the silent attempt rides on, so
 * nothing here is load-bearing and a wrong answer costs a click rather than an error.
 */

/** Query parameter AppLauncher reads the username from. Matches its `LOGIN_HINT_PARAM`. */
const LOGIN_HINT_PARAM = 'login_hint';

/** Query parameter AppLauncher reads the display language from. */
const LANGUAGE_PARAM = 'lang';

/**
 * QuickDial's space in the AppLauncher help centre, opened for a role with no page of its own set in the
 * hosting portal. The embedded frame is locked to whichever space it opens in.
 */
const HELP_SPACE = 'quickdial';

/** The two languages AppLauncher publishes. */
export type AppLauncherLanguage = 'sv' | 'en';

/**
 * Appends the signed-in username to an AppLauncher URL as a login hint.
 *
 * Returns the URL unchanged when there is no username to add, when the URL cannot be parsed, or when it
 * is not http(s) — the value identifies a user, so it is only ever attached to a destination we can see
 * is a real web address, never to a `javascript:` or `data:` URL that a misconfigured portal setting
 * could otherwise smuggle in. An existing hint is left alone rather than duplicated.
 */
export function withLoginHint(url: string | null | undefined, username: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  const hint = toHintableUsername(username);

  return hint ? setParam(url, LOGIN_HINT_PARAM, hint) : url;
}

/**
 * Appends the reader's language to an AppLauncher URL, so the page opens in the language they were
 * already reading rather than in AppLauncher's own default.
 *
 * <p>AppLauncher publishes only `sv` and `en`; QuickDial ships five locales, so the other three map to
 * English — the AppLauncher locale a Norwegian, Danish or Finnish reader is likelier to follow than
 * Swedish. An unrecognised value upstream is ignored and falls back to the visitor's stored locale, so
 * the mapping is a courtesy rather than a contract.</p>
 *
 * <p>A language already in the URL wins: it was put there by whoever configured the address, and this
 * is the weaker signal of the two.</p>
 */
export function withLanguage(url: string | null | undefined, language: string | null | undefined): string | null {
  if (!url) {
    return null;
  }

  const trimmedLanguage = language?.trim();

  if (!trimmedLanguage) {
    return url;
  }

  return setParam(url, LANGUAGE_PARAM, toAppLauncherLanguage(trimmedLanguage));
}

/**
 * Maps a QuickDial locale to the AppLauncher language a reader is likeliest to follow: Swedish for
 * Swedish, English for everything else (see {@link withLanguage}).
 */
export function toAppLauncherLanguage(language: string | null | undefined): AppLauncherLanguage {
  return language?.trim().toLowerCase().startsWith('sv') ? 'sv' : 'en';
}

/**
 * The origin of the AppLauncher help centre, or null when the configured address is missing or is not
 * http(s). Only the origin is kept: the setting is the portal address and is often given as its
 * subscriptions page, while the help pages always hang off the root. It is also the only origin the
 * embedded frame's messages are accepted from.
 */
export function helpCenterOrigin(portalUrl: string | null | undefined): string | null {
  const trimmed = portalUrl?.trim();

  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);

    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.origin : null;
  } catch {
    return null;
  }
}

export interface HelpCenterUrlOptions {
  portalUrl: string | null | undefined;
  language: string | null | undefined;
  username?: string | null | undefined;
  /** Frame mode: help content only, in the given theme. Omitted for the "open in a new tab" link. */
  embedded?: { theme: 'light' | 'dark' };
  /**
   * A help page to open instead of QuickDial's default space, as set per role in the hosting portal.
   * Ignored unless it is on the help centre's origin: that is the only origin the frame's CSP and
   * message check allow.
   */
  pageUrl?: string | null;
}

/** Placeholder in a configured help URL for the reader's help language. */
const LANGUAGE_PLACEHOLDER = '{lang}';

/**
 * A configured help page on the help centre's origin, in the reader's language, or null when it is
 * missing or lives anywhere else. The language follows either a `{lang}` placeholder or, for an address
 * copied straight from the help centre, its `/help/sv/` or `/help/en/` segment.
 */
function resolveHelpPage(pageUrl: string | null | undefined, origin: string, language: AppLauncherLanguage): URL | null {
  const trimmed = pageUrl?.trim();

  if (!trimmed) {
    return null;
  }

  let url: URL;

  try {
    url = new URL(trimmed.split(LANGUAGE_PLACEHOLDER).join(language));
  } catch {
    return null;
  }

  if (url.origin !== origin) {
    return null;
  }

  url.pathname = url.pathname.replace(/^\/help\/(sv|en)(?=\/|$)/, `/help/${language}`);
  return url;
}

/**
 * The address of QuickDial's space in the AppLauncher help centre, or null when no help centre is
 * configured.
 *
 * <p>The signed-in username travels differently in the two modes. The embedded frame reads it from the
 * fragment, as AppLauncher documents for framed help, where it never reaches a server log; the full
 * site reads the same query parameter as every other handoff ({@link withLoginHint}).</p>
 */
export function buildHelpCenterUrl({ portalUrl, language, username, embedded, pageUrl }: HelpCenterUrlOptions): string | null {
  const origin = helpCenterOrigin(portalUrl);

  if (!origin) {
    return null;
  }

  const helpLanguage = toAppLauncherLanguage(language);
  const url = resolveHelpPage(pageUrl, origin, helpLanguage) ?? new URL(`/help/${helpLanguage}/${HELP_SPACE}/`, origin);
  url.searchParams.delete(LOGIN_HINT_PARAM);

  if (!embedded) {
    url.searchParams.delete('embedded');
    url.searchParams.delete('theme');
    return withLoginHint(url.toString(), username);
  }

  url.searchParams.set('embedded', 'true');
  url.searchParams.set('theme', embedded.theme);

  const hint = toHintableUsername(username);

  if (hint) {
    url.hash = `${LOGIN_HINT_PARAM}=${encodeURIComponent(hint)}`;
  }

  return url.toString();
}

/**
 * The username trimmed, or null when it is absent or could not be a UPN. Whitespace and control
 * characters do not appear in a UPN, and AppLauncher drops such a hint anyway.
 */
function toHintableUsername(username: string | null | undefined): string | null {
  const trimmed = username?.trim();

  if (!trimmed || /[\s\p{Cc}]/u.test(trimmed)) {
    return null;
  }

  return trimmed;
}

/**
 * Sets one query parameter on a URL we can see is a real web address, leaving anything else untouched.
 *
 * A parameter already present is kept rather than overwritten: every caller here is adding a hint about
 * the reader, which is always the weaker claim next to a value someone put in the address deliberately.
 */
function setParam(url: string, name: string, value: string): string {
  let parsed: URL;

  try {
    // Resolved against the current origin where there is one, so a relative portal address still works;
    // `window` is absent under the node test environment, and an absolute URL needs no base anyway.
    parsed = typeof window === 'undefined' ? new URL(url) : new URL(url, window.location.origin);
  } catch {
    return url;
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return url;
  }

  if (parsed.searchParams.has(name)) {
    return url;
  }

  parsed.searchParams.set(name, value);

  return parsed.toString();
}
